import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, FileText, ChevronRight, AlertCircle, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { WorkflowGuidanceCard } from '@/components/intake/WorkflowGuidanceCard';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const STATUS_TONE = {
  INTAKE_DRAFT: { label: 'Draft', cls: 'bg-muted text-muted-foreground' },
  INTAKE_REVIEW: { label: 'In review', cls: 'bg-amber-100 text-amber-800' },
  ACTIVATED: { label: 'Activated', cls: 'bg-emerald-100 text-emerald-800' },
  INTAKE_DECLINED: { label: 'Declined', cls: 'bg-rose-100 text-rose-800' },
};

// P-4 — human-friendly labels for required intake sections.
// Keys mirror REQUIRED_SECTIONS in /app/backend/services/intake/promotion.py.
const REQUIRED_SECTION_LABELS = {
  demographics:  'Demographics',
  ndis_funding:  'NDIS & Funding',
  services:      'Services',
  contacts:      'Emergency Contacts',
  assignments:   'Assignments',
  consents:      'Consents',
};

function formatMissingSections(missing) {
  if (!Array.isArray(missing) || missing.length === 0) return '';
  const names = missing.map((k) => REQUIRED_SECTION_LABELS[k] || k);
  if (names.length === 1) return `${names[0]} missing`;
  if (names.length <= 3) return `${names.join(', ')} missing`;
  return `${names.slice(0, 3).join(', ')} … +${names.length - 3} more missing`;
}

export default function ParticipantIntakeListPage() {
  const [intakes, setIntakes] = useState([]);
  const [status, setStatus] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = status !== 'ALL' ? `?status=${status}` : '';
      const r = await api.get(`/intake/participants${params}`);
      setIntakes(r.data.items || []);
    } catch (e) {
      const code = e?.response?.data?.error?.code;
      if (code === 'FEATURE_DISABLED') {
        toast.error('Participant Intake Wizard is disabled for this organisation.');
      } else {
        toast.error('Could not load intakes.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = async () => {
    setCreating(true);
    try {
      const r = await api.post('/intake/participants', {});
      navigate(`/intake/participants/${r.data.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Could not create intake.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/intake/participants/${id}`);
      toast.success('Declined intake deleted.');
      setIntakes((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Could not delete intake.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="intake-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Participant Intake</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Guided multi-section intake for new participants. Save partial, activate when ready.
          </p>
        </div>
        <Button onClick={startNew} disabled={creating} data-testid="intake-new-btn">
          <Plus className="h-4 w-4 mr-1" /> {creating ? 'Creating…' : 'New Intake'}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="intake-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="INTAKE_DRAFT">Draft</SelectItem>
            <SelectItem value="INTAKE_REVIEW">In review</SelectItem>
            <SelectItem value="ACTIVATED">Activated</SelectItem>
            <SelectItem value="INTAKE_DECLINED">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* P-5 — Dismissible workflow guidance (pre-live polish). */}
      <WorkflowGuidanceCard
        storageKey="gc360_help_dismissed.intake_list"
        title="How Participant Intake works"
        steps={[
          'Pick any intake in Draft or In review.',
          'Complete the required sections highlighted in amber.',
          'Submit the intake for review when all required sections are filled.',
          'An Admin will then Activate the intake to create the live participant.',
        ]}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : intakes.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
            No intakes yet. Click <strong>New Intake</strong> to start one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {intakes.map((it) => {
            const tone = STATUS_TONE[it.status] || STATUS_TONE.INTAKE_DRAFT;
            const ready = it.readiness?.required_complete;
            const missing = it.readiness?.missing || [];
            const demo = it.promoted_participant_id ? '—' : (it.created_via_intake_id || it.id || '').slice(-8);
            return (
              <Card key={it.id} className="hover:bg-muted/30 cursor-pointer transition" onClick={() => navigate(`/intake/participants/${it.id}`)} data-testid={`intake-row-${it.id}`}>
                <CardContent className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={tone.cls}>{tone.label}</Badge>
                    <div>
                      <div className="font-medium">Intake #{demo}</div>
                      <div className="text-xs text-muted-foreground">
                        {it.status === 'ACTIVATED' ? (
                          <>Activated — participant created</>
                        ) : it.status === 'INTAKE_DECLINED' ? (
                          <>Declined</>
                        ) : ready ? (
                          <span className="text-emerald-700"><CheckCircle2 className="h-3 w-3 inline mr-0.5" />Ready to activate</span>
                        ) : (
                          <span className="text-amber-700" data-testid={`intake-missing-label-${it.id}`}><AlertCircle className="h-3 w-3 inline mr-0.5" />{formatMissingSections(missing) || `${missing.length} required section(s) missing`}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {it.status === 'INTAKE_DECLINED' && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={(e) => e.stopPropagation()}
                            disabled={deletingId === it.id}
                            data-testid={`intake-delete-btn-${it.id}`}
                            title="Delete declined intake"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid="intake-delete-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this declined intake?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Intake #{demo} will be permanently deleted. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="intake-delete-cancel">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-rose-600 hover:bg-rose-700"
                              onClick={() => handleDelete(it.id)}
                              data-testid="intake-delete-confirm"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
